import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, Loader2 } from "lucide-react";
import axios from "axios";
import { useLanguage } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccess() {
  const { t } = useLanguage();
  const { clearCart } = useCart();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | success | failed
  const [attempts, setAttempts] = useState(0);

  const pollStatus = useCallback(async () => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        setStatus("success");
        clearCart();
      } else if (res.data.status === "expired") {
        setStatus("failed");
      } else if (attempts < 5) {
        setAttempts((a) => a + 1);
      } else {
        setStatus("failed");
      }
    } catch {
      if (attempts < 5) {
        setAttempts((a) => a + 1);
      } else {
        setStatus("failed");
      }
    }
  }, [sessionId, attempts, clearCart]);

  useEffect(() => {
    if (status === "polling") {
      const timer = setTimeout(pollStatus, 2000);
      return () => clearTimeout(timer);
    }
  }, [status, attempts, pollStatus]);

  return (
    <div className="px-6 md:px-12 lg:px-24 py-24 md:py-32 text-center min-h-[60vh] flex flex-col items-center justify-center" data-testid="checkout-success-page">
      {status === "polling" && (
        <>
          <Loader2 size={40} strokeWidth={1} className="animate-spin text-[#9CA3AF] mb-6" />
          <h1
            className="text-2xl md:text-3xl mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("checkout.processing")}
          </h1>
        </>
      )}

      {status === "success" && (
        <>
          <CheckCircle size={48} strokeWidth={1} className="text-green-700 mb-6" />
          <h1
            className="text-2xl md:text-3xl mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="checkout-success-title"
          >
            {t("checkout.success")}
          </h1>
          <p className="text-sm text-[#9CA3AF] tracking-wide mb-8">
            {t("checkout.order_confirmed")}
          </p>
          <Link to="/shop" className="btn-luxury" data-testid="checkout-continue-btn">
            {t("checkout.continue")}
          </Link>
        </>
      )}

      {status === "failed" && (
        <>
          <h1
            className="text-2xl md:text-3xl mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("checkout.failed")}
          </h1>
          <Link to="/cart" className="btn-luxury" data-testid="checkout-back-to-cart">
            {t("common.back")}
          </Link>
        </>
      )}
    </div>
  );
}
